import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Mail, Clock, Send, CheckCircle2, BarChart3, Loader2, Save, Plus, Trash2, ChevronRight, Eye } from 'lucide-react';

const TEMPLATE_LABELS = {
  thank_you: '🙏 Agradecimiento',
  review_request: '⭐ Solicitud de reseña',
  nps: '📊 Encuesta NPS',
  special_offer: '🎁 Oferta especial',
};

const CHANNEL_ICONS = { email: Mail, whatsapp: '💬' };

const PREVIEW_CONTENT = {
  thank_you: {
    subject: '¡Gracias por visitarnos!',
    body: `Estimado/a {{nombre}},

Ha sido un placer tenerte en nuestro restaurante. Esperamos que hayas disfrutado de la experiencia y que podamos verte pronto de nuevo.

Fue un honor atenderte.

Con cariño,
El equipo de {{restaurante}}`,
  },
  review_request: {
    subject: '¿Nos dejas tu opinión?',
    body: `Hola {{nombre}},

Nos encantaría saber qué te pareció tu visita. Tu opinión nos ayuda a mejorar y sirve de guía para otros comensales.

Haz clic aquí para dejarnos una reseña en Google: [enlace]

¡Muchas gracias!
{{restaurante}}`,
  },
  nps: {
    subject: 'Cuéntanos cómo fue tu experiencia',
    body: `Hola {{nombre}},

En una escala del 0 al 10, ¿cuánto nos recomendarías a tus amigos y familiares?

0 - 1 - 2 - 3 - 4 - 5 - 6 - 7 - 8 - 9 - 10

Tu respuesta tarda menos de 30 segundos y nos ayuda mucho.

Gracias,
{{restaurante}}`,
  },
  special_offer: {
    subject: '🎁 Tenemos algo especial para ti',
    body: `Hola {{nombre}},

Han pasado 7 días desde tu visita y nos gustaría verte de nuevo. Por eso, queremos ofrecerte un descuento especial en tu próxima visita.

Menciona este email al hacer tu reserva y obtén un 10% de descuento.

¡Reserva aquí! [enlace]

{{restaurante}}`,
  },
};

export default function PostVisitFollowup() {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState({ total: 0, sent: 0, opened: 0, open_rate: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewSeq, setPreviewSeq] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgR, stR] = await Promise.all([api.get('/followups/config'), api.get('/followups/stats')]);
      setConfig(cfgR.data);
      setStats(stR.data);
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try { await api.patch('/followups/config', config); toast.success('Configuración guardada'); }
    catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const toggleSeq = (id, val) => setConfig(c => ({
    ...c, sequences: c.sequences.map(s => s.id === id ? { ...s, enabled: val } : s)
  }));

  const updateSeq = (id, key, val) => setConfig(c => ({
    ...c, sequences: c.sequences.map(s => s.id === id ? { ...s, [key]: val } : s)
  }));

  const addSeq = () => setConfig(c => ({
    ...c, sequences: [...c.sequences, {
      id: Date.now().toString(), name: 'Nueva secuencia', delay_hours: 72,
      enabled: false, channel: 'email', template: 'thank_you'
    }]
  }));

  const removeSeq = (id) => setConfig(c => ({ ...c, sequences: c.sequences.filter(s => s.id !== id) }));

  if (loading) return (
    <div className="h-screen flex flex-col"><TopBar title="Seguimiento Post-visita" helpSection="followup" />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar title="Seguimiento Post-visita" helpSection="followup" actions={
        <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white h-8">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Guardar
        </Button>
      } />

      <div className="flex-1 p-6 max-w-5xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-blue-500 flex items-center justify-center flex-shrink-0">
              <Mail className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Seguimiento Post-visita</h1>
              <p className="text-slate-500 text-sm">Secuencias automáticas de emails tras cada visita</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-sm text-slate-600">Activar seguimientos</Label>
            <Switch checked={!!config?.enabled} onCheckedChange={v => setConfig(c => ({ ...c, enabled: v }))} />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { l: 'Emails programados', v: stats.total, icon: Clock, c: 'text-slate-600', bg: 'bg-slate-50' },
            { l: 'Enviados', v: stats.sent, icon: Send, c: 'text-blue-600', bg: 'bg-blue-50' },
            { l: 'Abiertos', v: stats.opened, icon: CheckCircle2, c: 'text-green-600', bg: 'bg-green-50' },
            { l: 'Tasa de apertura', v: `${stats.open_rate}%`, icon: BarChart3, c: 'text-orange-600', bg: 'bg-orange-50' },
          ].map(s => (
            <Card key={s.l} className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center mb-3", s.bg)}>
                  <s.icon className={cn("h-4 w-4", s.c)} />
                </div>
                <div className="text-2xl font-bold text-slate-800">{s.v}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.l}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Timeline */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Secuencia de mensajes</CardTitle>
            <Button size="sm" variant="outline" onClick={addSeq} className="h-8 text-xs">
              <Plus className="h-3.5 w-3.5 mr-1" /> Agregar paso
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {config?.sequences?.map((seq, idx) => (
              <div key={seq.id} className="flex gap-4">
                {/* Timeline dot */}
                <div className="flex flex-col items-center">
                  <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0",
                    seq.enabled ? "bg-orange-500 text-white" : "bg-slate-200 text-slate-500")}>
                    {idx + 1}
                  </div>
                  {idx < (config?.sequences?.length - 1) && (
                    <div className="w-0.5 h-full bg-slate-200 mt-2 min-h-6" />
                  )}
                </div>
                {/* Card */}
                <div className={cn("flex-1 border rounded-xl p-4 mb-4 transition-all",
                  seq.enabled ? "border-orange-200 bg-orange-50/50" : "border-slate-200 bg-white")}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-[10px] text-slate-400 mb-1 block">Nombre</Label>
                          <Input value={seq.name} onChange={e => updateSeq(seq.id, 'name', e.target.value)} className="text-xs h-8" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-400 mb-1 block">Plantilla</Label>
                          <Select value={seq.template} onValueChange={v => updateSeq(seq.id, 'template', v)}>
                            <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(TEMPLATE_LABELS).map(([k, l]) => (
                                <SelectItem key={k} value={k} className="text-xs">{l}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-400 mb-1 block">Enviar después de (horas)</Label>
                          <Input type="number" min={0} value={seq.delay_hours}
                            onChange={e => updateSeq(seq.id, 'delay_hours', +e.target.value)} className="text-xs h-8" />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => setPreviewSeq(previewSeq === seq.id ? null : seq.id)}
                        className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400 hover:text-slate-600">
                        <Eye className="h-4 w-4" />
                      </button>
                      <Switch checked={!!seq.enabled} onCheckedChange={v => toggleSeq(seq.id, v)} />
                      <button onClick={() => removeSeq(seq.id)} className="p-1.5 rounded-lg hover:bg-red-100 text-red-400">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {/* Preview */}
                  {previewSeq === seq.id && PREVIEW_CONTENT[seq.template] && (
                    <div className="mt-3 p-3 bg-white border border-slate-200 rounded-xl text-xs">
                      <p className="font-semibold text-slate-700 mb-1">Asunto: {PREVIEW_CONTENT[seq.template].subject}</p>
                      <pre className="text-slate-500 whitespace-pre-wrap font-sans text-xs leading-relaxed">{PREVIEW_CONTENT[seq.template].body}</pre>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
